import { Component, OnInit, Input, HostListener, ViewChild, ElementRef, AfterViewInit, OnDestroy, Inject } from '@angular/core';
import { LoggerService } from '@wdpr/ra-angular-logger';
import get from 'lodash-es/get';
import isEmpty from 'lodash-es/isEmpty';
// using resize-observer-polyfill to support safari 12
import ResizeObserver from 'resize-observer-polyfill';

import { Animation } from './animation.interface';
import { AnimationService } from '@finder/shared/components/animations/animation-service/animation.service';
import { ViewportDetectionService } from '@finder/shared/services/viewport-detection/viewport-detection.service';
import { CommonModule, DOCUMENT } from '@angular/common';
import { timeout } from '@finder/shared/utils/time/timeout';
import { InViewportDirective } from '@finder/shared/directives/in-viewport/in-viewport.directive';
import { WindowRef } from '@finder/shared/services/window-ref/window-ref.service';
import { SsrService } from '@finder/shared/services/ssr/ssr.service';

@Component({
    selector: 'finder-animation',
    templateUrl: './animation.component.html',
    styleUrls: ['./animation.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        InViewportDirective,
    ]
})
export class AnimationComponent implements OnInit, AfterViewInit, OnDestroy {

    @Input() data: Animation;
    @ViewChild('content') content: ElementRef;

    wrapperStyles;
    subscribeToInViewport = true;
    animateClass = false;
    spriteStyles;
    contentStyles: any = {};
    animationCount;
    viewportOffset = 0;
    animationStyles: any;
    hideIfMobile = false;

    private animateStyles;
    private animateContentStyles;
    private resolveAnimation;
    private rejectAnimation;
    private resizeObserver;

    constructor(
        private animationService: AnimationService,
        private viewportDetection: ViewportDetectionService,
        private logger: LoggerService,
        private elementRef: ElementRef,
        private winRef: WindowRef,
        private ssrService: SsrService,
        @Inject(DOCUMENT) private document: Document,
    ) {}

    @HostListener('transitionend', ['$event'])
    onTransitionEnd($event) {
        if ($event.target.className.indexOf('animation-sprite') >= 0) {
            this.animateToggle(false);
        }
    }

    @HostListener('animationend', ['$event'])
    onAnimationEnd($event) {
        if ($event.target.parentElement.className.indexOf('simple-sprite') >= 0) {
            // Reset back to last frame
            $event.target.style.setProperty('top', 'calc(var(--animation-height) * 2)');
        }

        if ($event.target.className.indexOf('animation-sprite') >= 0) {
            this.resolveAnimation();
            if (this.animationCount === 0 && this.resizeObserver) {
                this.resizeObserver.disconnect();
            }
        }

        if ($event.target.className.indexOf('content') >= 0) {
            this.animateToggle(false);
        }
    }

    ngOnInit() {
        const isMobile = this.viewportDetection.isMobile();
        this.hideIfMobile = get(this.data, 'hideInMobile', false) && isMobile;
        this.animationStyles = this.animationService.getAnimation(this.data, isMobile);
        this.saveAnimationStyles();

        // viewportOffset is a percentage, we need to calculate the offset in pixels
        // so the offset will work well in mobile and desktop.
        if (this.data.viewportOffset) {
            this.viewportOffset =
                this.viewportDetection.getViewportSize().height * (parseInt(this.data.viewportOffset, 10) / 100);
        }

        // by default an animation will play only once
        const repeatStr = this.data.repeat || '0';
        const repeat = ['infinite', 'infinity', '-1']
            .includes(repeatStr.toLowerCase()) ?
            Infinity :
            parseInt(repeatStr, 10);
        this.animationCount = Math.max(repeat, 1);
    }

    saveAnimationStyles() {
        // @SSR: Wait till we're in the browser for this bit
        if (this.ssrService.isSSR()) {
            return;
        }

        if (this.animationStyles) {
            this.spriteStyles = this.animationStyles.spriteStyles;
            this.wrapperStyles = this.animationStyles.wrapperStyles;
            this.animateStyles = this.animationStyles.animateStyles;
            this.animateContentStyles = this.animationStyles.animateContentStyles;

            // Preload sprite
            const bgUrl = (this.spriteStyles['background-image'] || '')
                .match(/url\("(.*)"\)/).pop();
            if (bgUrl) {
                const img = new Image();
                img.src = bgUrl;
            }

            // Check for component styling
            const componentStyles = this.animationStyles.componentStyles;
            if (!isEmpty(componentStyles)) {
                const el = this.elementRef.nativeElement;
                el.style.setProperty('--animation-width', componentStyles.width);
                el.style.setProperty('--animation-height', componentStyles.height);
                el.style.setProperty('--animation-inset', componentStyles.inset);
                if (componentStyles.insetMobile) {
                    el.style.setProperty('--animation-mobile-inset', componentStyles.insetMobile);
                }

                // Set height for pop-up keyframes
                this.document.documentElement.style.setProperty('--animation-height', this.animationStyles.componentStyles.height);
            }
        } else {
            this.logger.error('animation not found, transition name:', get(this.data, 'transition'));
        }
    }

    ngAfterViewInit() {
        // only add resize observer for slide-block-lr animations like buzz
        // in toy story land
        if (this.data.transition === 'slide-block-lr') {
            this.resizeObserver = new ResizeObserver(entries => {
                entries.forEach(entry => {
                    const { contentRect } = entry;
                    this.animationStyles = this.animationService.getAnimation(
                        this.data,
                        this.viewportDetection.isMobile(),
                        contentRect.height);
                    this.saveAnimationStyles();
                });
            });
            this.resizeObserver.observe(this.content.nativeElement);
        }
    }

    ngOnDestroy() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    }

    animateToggle(animate: boolean = true) {
        if (animate) {
            this.startAnimation();
        } else {
            this.animateClass = false;
            this.spriteStyles = this.animationStyles.spriteStyles;
            this.contentStyles = {};
            this.animationCount--;
            this.resolveAnimation();
            if (this.animationCount === 0 && this.resizeObserver) {
                this.resizeObserver.disconnect();
            }
        }
    }

    private startAnimation() {
        const animationPromise = new Promise((resolve, reject) => {
            this.resolveAnimation = resolve;
            this.rejectAnimation = reject;
        });
        if (this.animationService.requestSlot(animationPromise)) {
            this.animateClass = true;
            this.spriteStyles = Object.assign(
                {},
                this.spriteStyles,
                this.animateStyles
            );
            this.contentStyles = this.animateContentStyles;

            // Check for single frame animations
            if (this.data.frames === '1') {
                this.resolveAnimation();
                if (this.resizeObserver) {
                    this.resizeObserver.disconnect();
                }
            } else if (this.animationCount === Infinity) {
                // Wait for the first cycle of playback and then finish up
                timeout(() => {
                    this.resolveAnimation();
                    if (this.resizeObserver) {
                        this.resizeObserver.disconnect();
                    }
                }, parseInt(this.data.duration, 10));
            }
        } else {
            this.subscribeToInViewport = false;
            this.animationService
                .getOngoingAnimationPromise()
                .then(() => this.subscribeToInViewport = true);
        }
    }
}
